import ReactImageGallery, { ReactImageGalleryItem } from 'react-image-gallery';
import { RenderLeftNav, RenderRightNav } from '../nav';
import { FullscreenButton } from '../fullscreen';
import { Dialog } from '@material-ui/core';
import styled from 'styled-components';
import { SectionNotes } from 'services/api/vehicle-sections/types';
import { CarouselItem } from 'services/viewVehicleSlice/viewVehicleSlice';
import { FullScreenDescription } from './FullScreenDescription';
import AutoPlayVideo from 'components/AutoPlayVideo';

const StyledDialog = styled(Dialog)`
  .fullscreen-image-gallery {
    height: 95vh;
    max-height: 95vh;
    max-width: 95vw;
    aspect-ratio: 4 / 3;
    border-radius: 30px;
    padding: 0;
  }

  @media (max-width: 1024px) {
    .fullscreen-image-gallery {
      height: auto;
      width: 100%;
      margin: 0 20px;
    }
  }
`;

type Props = {
  relatedRef?: React.RefObject<ReactImageGallery>;
  close: () => void;
  images: CarouselItem[];
  initialFullScreenIndex: number | null;
  isOpened: boolean;
  showCounter?: boolean;
  renderItemOverlay?: (imageId: number) => React.ReactNode;
};

export const FullScreenCarouselDialog = (props: Props) => {
  const { isOpened, initialFullScreenIndex, close } = props;

  return (
    <StyledDialog
      classes={{ paperScrollPaper: 'fullscreen-image-gallery' }}
      open={isOpened}
      onBackdropClick={close}
      onClose={close}
    >
      {initialFullScreenIndex != null && <FullScreenCarousel {...props} />}
    </StyledDialog>
  );
};

const FullScreenCarousel = ({
  relatedRef,
  close,
  images,
  initialFullScreenIndex,
  showCounter = true,
  renderItemOverlay
}: Props) => {
  const handleSlide = (newI: number) => {
    if (!relatedRef || !relatedRef.current) return;
    relatedRef.current.slideToIndex(newI);
  };

  return (
    <ReactImageGallery
      slideDuration={0}
      showPlayButton={false}
      showFullscreenButton={true}
      renderLeftNav={(onclick, disabled) => <RenderLeftNav isFullScreen onClick={onclick} disabled={disabled} />}
      renderRightNav={(onclick, disabled) => <RenderRightNav isFullScreen onClick={onclick} disabled={disabled} />}
      renderFullscreenButton={(onclick, isFullScreen) => (
        <FullscreenButton type="fullscreen" onClick={close} isFullscreen={isFullScreen} />
      )}
      renderItem={item => (
        <CustomItem {...item} description={item.description as any} renderOverlay={renderItemOverlay} />
      )}
      items={images.map(i => ({
        original: i.url,
        thumbnail: i.url_thumb,
        description: 'notes' in i ? (i.notes as any) : undefined,
        type: i.type as any,
        imageId: typeof i.id === 'number' ? i.id : undefined
      }))}
      showIndex={showCounter}
      additionalClass="fullscreen"
      startIndex={initialFullScreenIndex!}
      useBrowserFullscreen={false}
      showThumbnails={false}
      onSlide={handleSlide}
      infinite
    />
  );
};

const CustomItem = ({
  description,
  type,
  imageId,
  renderOverlay,
  ...props
}: ReactImageGalleryItem & {
  description?: SectionNotes;
  type?: string;
  imageId?: number;
  renderOverlay?: (imageId: number) => React.ReactNode;
}) => {
  if (type === 'exterior.video') {
    return (
      <div className="image-gallery-image" style={{ background: '#000' }}>
        <AutoPlayVideo src={props.original} disableNativeFullscreen />
      </div>
    );
  }

  return (
    <div className="image-gallery-image" style={{ position: 'relative' }}>
      <img src={props.original} />
      {imageId != null && renderOverlay?.(imageId)}
      {description && <FullScreenDescription type={type} notes={description} />}
    </div>
  );
};
